import { useCallback, useEffect, useRef, useState } from 'react';

import { commitSpan } from './commit-span';
import { previewSpanFor } from './drag-math';
import { isPreviewStale, sameSpan, useSpanPreview } from './use-span-preview';

import type { DragMode } from './drag-math';
import type { SpanPreview } from './use-span-preview';
import type { GanttItem, GanttPatch, Span, TimeScale } from '../types';
import type React from 'react';

export type { DragMode };

export type BarDrag = {
  mode: DragMode | null;
  /**
   * The span currently drawn — whoever set it. With an internal store (the default) that is only
   * ever this hook's own gesture. With a store SHARED with `useBarKeyboard` (what `Bar` does) it
   * is also the keyboard's pending preview between gestures, which is exactly the point: there is
   * one preview, not two racing ones. A shared-store caller should read the store rather than this
   * field, so the source of truth is unambiguous at the call site.
   */
  previewSpan?: Span;
  onPointerDown: (mode: DragMode) => (event: React.PointerEvent) => void;
};

/**
 * Owns pointer capture, the pixel-to-day conversion, and the commit call — nothing else. Every
 * rule about what a delta MEANS (clamping, minimum span, which edge moved, what a single-date
 * item does) lives in `drag-math.ts` so it is shared with the keyboard path without this hook
 * being involved.
 *
 * The preview lives in React state and only there: nothing here writes an inline style, so the
 * DOM stays a pure function of state through the whole gesture, including a rejected commit.
 *
 * C2 — THE GESTURE MEASURES FROM WHAT IS DRAWN, NOT FROM `row.span`. `onPointerDown` seeds
 * `latestPreview` from the preview store's current span when there is one, falling back to the
 * item's own span otherwise. That is what makes a drag started on top of a pending keyboard
 * preview move the bar from where the user can SEE it rather than snapping the accumulated
 * keyboard days away and measuring from the item's real dates. The commit still DIFFS against the
 * item's real span (`span`, captured at pointerdown), so the resulting patch carries the whole
 * distance travelled — keyboard days included — rather than only the pointer's share of it.
 *
 * Adopting the drawn span is also how this hook CANCELS the keyboard: the store is single-valued,
 * so `open()` here takes ownership of it outright and there is no second preview left to reappear
 * when this one clears. `Bar` completes the pair by suspending `useBarKeyboard` while `mode` is
 * non-null; see that hook's doc-comment for why the keyboard is refused mid-drag rather than
 * allowed to cancel a live gesture.
 */
export const useBarDrag = (args: {
  span: Span | undefined;
  item: GanttItem;
  scale: TimeScale;
  disabled?: boolean;
  onCommit?: (patches: GanttPatch[]) => Promise<void> | void;
  /**
   * The bar's shared preview store. Omit it and this hook keeps a private one, which is the right
   * shape for a caller that runs no other gesture (`GroupBar`, a custom renderer using drag alone).
   */
  preview?: SpanPreview;
}): BarDrag => {
  const { span, item, scale, disabled, onCommit } = args;

  // Always called, then discarded when the caller supplied one — a hook cannot be called
  // conditionally, and an unused store costs one `useState` slot.
  const ownPreview = useSpanPreview();
  const preview = args.preview ?? ownPreview;

  // Read through a ref inside `onPointerDown`, never closed over: the store's `span` changes on
  // every frame of a gesture, and putting it in the callback's dependency array would rebuild the
  // handler continuously for a value only ever read once, at gesture start.
  const previewRef = useRef(preview);
  previewRef.current = preview;

  const [dragMode, setDragMode] = useState<DragMode | null>(null);

  // Tracks whether a gesture is currently live, and how to tear it down. A ref rather than
  // `dragMode` state so the re-entrancy guard below is synchronous — no risk of a second
  // `pointerdown` landing in the gap before a state update from the first has been applied.
  // The unmount effect reads the same ref: if the dragged row unmounts mid-gesture (a group
  // collapsing, a realtime delete, a route change), this is what stops the `window` `keydown`
  // listener from outliving the component and holding a stale closure over `item`/`span`/`onCommit`.
  //
  // N8: this is a RE-ENTRANCY guard and nothing else now. "Is anything currently held" is the
  // right question for refusing a second concurrent `pointerdown` — there is one `dragMode` and
  // one listener set per bar, so a second live gesture is genuinely unrepresentable. It is the
  // WRONG question for releasing what a gesture took, which is an identity question; see
  // `releaseDragMode`.
  const activeRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  // See `use-span-preview.ts`'s identical guard for why the `true` on SETUP matters as much as the
  // `false` on teardown.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      cleanupRef.current?.();
    };
  }, []);

  const onPointerDown = useCallback(
    (mode: DragMode) => (event: React.PointerEvent) => {
      // No span to drag from (an undated row), a disabled item, a non-primary button (so a
      // right- or middle-click never drags a bar), and an already-live gesture (so a second
      // `pointerdown` can't attach a second listener set and double-commit) all mean the gesture
      // never starts: no listeners attached, no state written, `mode` stays `null` for the caller.
      if (disabled || !span || event.button !== 0 || activeRef.current) return;

      const store = previewRef.current;

      // C2. The origin is the span the user can SEE. A pending keyboard preview is adopted, so a
      // drag continues from where the arrows left the bar. A preview whose base no longer matches
      // the item's current span is NOT adopted — it was computed against dates that have since
      // moved, so continuing from it would compound one stale edit with a fresh gesture; the
      // gesture restarts from the item's real span instead, which is also what the bar will be
      // drawing from the first `open()` below onwards.
      const origin = !isPreviewStale(store, span) && store.span ? store.span : span;

      // N7. `true` when this gesture STARTED on a pending keyboard preview rather than on the
      // item's own dates. A release that never moved is then a bare click landing on someone
      // else's uncommitted edit — see `finish()` for what that does and why.
      const adoptedPreview = !sameSpan(origin, span);

      const element = event.currentTarget;
      const pointerId = event.pointerId;
      const startClientX = event.clientX;
      let latestPreview: Span = origin;

      activeRef.current = true;

      // Real browsers implement this on every element; jsdom — the only environment this project
      // tests in that lacks it — has no such method at all, so the optional call is a no-op there
      // instead of a thrown TypeError. Nothing about the drag depends on capture actually taking
      // effect: the listeners below are attached directly to `element`, which is what capture
      // would retarget events to anyway.
      element.setPointerCapture?.(pointerId);
      setDragMode(mode);
      // Takes ownership of the shared store for the duration of the gesture, recording the item's
      // CURRENT span as the base every preview written below is measured against.
      store.open(span, origin);

      const applyDelta = (clientX: number) => {
        const dayDelta = scale.snapDays(clientX - startClientX);
        latestPreview = previewSpanFor(origin, mode, dayDelta);
        store.open(span, latestPreview);
      };

      const handleMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        applyDelta(moveEvent.clientX);
      };

      const cleanup = () => {
        element.removeEventListener('pointermove', handleMove);
        element.removeEventListener('pointerup', handleUp);
        element.removeEventListener('pointercancel', handleCancel);
        window.removeEventListener('keydown', handleKeyDown);
        element.releasePointerCapture?.(pointerId);
        activeRef.current = false;
        cleanupRef.current = null;
      };

      // Ends THIS gesture's ownership of the two things it took over: this hook's `dragMode`, and
      // the shared preview store. Called synchronously on cancel/Escape, and asynchronously as
      // `commitSpan`'s `onSettle` — which is where the guards earn their keep.
      //
      // `mountedRef`: the component may have unmounted while `onCommit` was still in flight (a
      // promise, unlike an event listener, isn't stopped by `cleanup`'s `removeEventListener`
      // calls), so neither write may land in a dead instance. `store.clear()` carries its own
      // identical guard.
      //
      // N3/N8 — ONE IDENTITY QUESTION, ASKED OF BOTH PIECES OF SHARED STATE. Everything this bar's
      // two gestures share (`dragMode`, and through it `useBarKeyboard`'s `suspended`; plus the
      // preview store itself) is released on the SAME predicate: `store.isCurrent(version)` —
      // "nothing has written to the preview since I took this version", i.e. "I am still the
      // owner". Both settle paths are asynchronous, so by the time either runs a LATER gesture may
      // own the bar; press, release, press again before the first `step.update` resolves is an
      // ordinary thing to do on a slow connection.
      //
      // N8 — WHY `!activeRef.current` WAS THE WRONG PREDICATE, AND IS GONE. `dragMode` used to be
      // released whenever nothing was CURRENTLY HELD, which is a global question ("is anything
      // live?") standing in for an identity one ("is this gesture's own commit the one settling?").
      // Two ordinary sequential drags, both RELEASED before either commit lands, separate the two:
      // nothing is held, so the older gesture's settle nulled `dragMode` and un-suspended the
      // keyboard while the newer gesture's commit was still outstanding. An arrow key then
      // recomputed from the base the older commit had just written (the newer preview reads stale,
      // so `step` restarts from the item's fresh dates) and `Enter` sent a THIRD commit for a span
      // no gesture ever drew — which then survived, because the newer gesture's settle correctly
      // refuses to clear a store someone else has written since. `bar.test.tsx` pins the sequence.
      //
      // The two guards were previously deliberately different, on the argument that `dragMode`
      // must never be left stuck non-null (the bar would keep `data-dragging` and the keyboard
      // would stay suspended forever). That risk does not survive the unification, because the two
      // are now released together by the same owner: while `dragMode` is non-null the keyboard is
      // suspended, so `useBarKeyboard` cannot write to the store at all (neither `step`, nor
      // `commit`, nor `onBlur`), and the only writer left is a subsequent pointer gesture — whose
      // own `pointerdown` unconditionally calls `store.open`, taking ownership of BOTH and
      // carrying the obligation to release them. Version numbers are monotonic, so a superseded
      // version can never become current again: whoever wrote last is the one that ends the
      // gesture, and there is always exactly one of them.
      const releaseDragMode = (version: number) => {
        if (mountedRef.current && store.isCurrent(version)) setDragMode(null);
      };

      const releaseGesture = (version: number) => {
        releaseDragMode(version);
        if (store.isCurrent(version)) store.clear();
      };

      // Committing clears the preview either way: on success the item's own (now-updated) span
      // takes over on the next render, and on rejection there is nothing left to show in its
      // place — the caller is responsible for surfacing the failure. The sequence itself (compute
      // patches, skip the call entirely when empty, invoke `onCommit`, settle on both resolve and
      // reject) lives in `commit-span.ts`, shared with `use-bar-keyboard.ts`, so the two input
      // methods cannot diverge on commit semantics while agreeing on the day math.
      //
      // C2. `span`, not `origin`, is the diff base: the patch has to describe the move from the
      // item's REAL dates to what is drawn, so an adopted keyboard preview is carried into it.
      //
      // N6 — WHY THERE IS NO `isPreviewStale` REFUSAL HERE, WHILE THE KEYBOARD PATH HAS ONE.
      // Deliberate and asymmetric, not an omission. `useBarKeyboard.commit()` refuses a preview
      // whose base has moved because a keyboard preview SITS: it was computed at some earlier
      // keystroke, the user may have looked away since, and `Enter` is a separate act that can
      // land long after the dates changed underneath it. A drag preview does not sit. It is
      // recomputed on every `pointermove`, the bar draws exactly `latestPreview`, and the release
      // is the same act as the drawing — so "commit what the bar is drawing" holds here without
      // any staleness test, which is the property C2 is actually about. What a mid-drag change to
      // `span` costs is only WHICH FIELDS the patch carries (`patchesFor` takes its VALUES from
      // the preview, absolute; the base span only decides whether an edge changed), so the failure
      // mode is a dropped edge on a conflict, not a value nobody chose.
      //
      // Adding the refusal was tried and rejected on a counter-example: press, release (commit in
      // flight), press again — the ordinary double-drag N3 above is about. The second gesture's
      // captured `span` is the pre-commit one, the first commit's optimistic write lands mid-
      // gesture, and a staleness refusal would silently discard a drag the user is in the middle
      // of performing. Last-write-wins is the conflict policy for a LIVE gesture; refuse-and-
      // revert is the policy for a PENDING one. `bar.test.tsx` pins both directions.
      //
      // N7 — A BARE CLICK ON A PENDING KEYBOARD PREVIEW LEAVES IT PENDING. The earlier version of
      // this hook committed it, on the argument that the only alternative was to discard it and
      // yank the bar backwards under the cursor. That is a false dichotomy: the third option is to
      // do NEITHER — keep the preview exactly as drawn, hand ownership back to the keyboard, and
      // let `Enter` / `Tab` / `Escape` resolve it as they always could. Decisive against
      // committing: `bar.tsx`'s `KEYBOARD_HINT`, spoken on focus and carried machine-readably in
      // `aria-keyshortcuts`, names ONE save key and TWO discard paths. A click is none of the
      // three, there is no undo behind it, and clicking a bar to reposition the pointer is an
      // ordinary thing for the keyboard-and-mouse user this whole path exists to serve. Writing on
      // an unannounced fourth path contradicts a contract we ship to users.
      //
      // Scoped narrowly, to a release that moved NOTHING (`latestPreview === origin`) on a gesture
      // that ADOPTED someone else's preview. A gesture that started on the item's own dates has no
      // pending edit to preserve and falls through to `commitSpan`, which skips the network call
      // for an empty patch set and clears — the zero-delta click's behaviour is unchanged there.
      // A drag that travels and comes back to the item's REAL dates also falls through, and
      // correctly discards: the user put the bar back where it started.
      //
      // `setDragMode(null)` still runs (via `releaseGesture`) so `useBarKeyboard`'s `suspended`
      // goes false and the preview is reachable again; the store is deliberately left alone.
      const finish = () => {
        cleanup();

        if (adoptedPreview && sameSpan(latestPreview, origin)) {
          // Synchronous, and this gesture's own `open` is by construction the store's latest
          // write (a pointerdown always opens; any later gesture would have been refused by
          // `activeRef` until `cleanup()` above), so the version taken here is current and the
          // release lands — the identity check is uniform rather than merely ceremonial.
          releaseDragMode(store.version());

          return;
        }

        const version = store.version();

        commitSpan({
          item,
          span,
          preview: latestPreview,
          onCommit,
          onSettle: () => releaseGesture(version),
        });
      };

      const handleUp = (upEvent: PointerEvent) => {
        if (upEvent.pointerId !== pointerId) return;
        finish();
      };

      const handleCancel = (cancelEvent: PointerEvent) => {
        if (cancelEvent.pointerId !== pointerId) return;
        cleanup();
        releaseGesture(store.version());
      };

      const handleKeyDown = (keyEvent: KeyboardEvent) => {
        if (keyEvent.key !== 'Escape') return;
        cleanup();
        releaseGesture(store.version());
      };

      cleanupRef.current = cleanup;
      element.addEventListener('pointermove', handleMove);
      element.addEventListener('pointerup', handleUp);
      element.addEventListener('pointercancel', handleCancel);
      window.addEventListener('keydown', handleKeyDown);
    },
    [disabled, span, item, scale, onCommit],
  );

  return { mode: dragMode, previewSpan: preview.span, onPointerDown };
};
